/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Divider, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { IdpButton } from '../idp-button';

import { getMicrosoftConfig, getGoogleConfig, getFacebookConfig, IdpConfigParams, type IdpButtonConfig } from '../../utils/get-provider-configs';
import { getAbContext } from '../../utils/get-ab-context';
import { getStoredLastLoginAuthType, storeLastLoginAuthType } from '../../utils/last-login-auth-type';

const useStyles = makeStyles({
  divider: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
});

export type IdpKey = 'MSA' | 'Google' | 'Facebook';

export type IdpButtonListProps = {
  providers: IdpKey[];
  scenario: 'signup' | 'signin' | 'userInvitation';
  // For the userInvitation scenario only: switches button copy between
  // "Continue with X" (create) and "Sign in with X" (signin). Behavior is identical.
  invitationVariant?: 'create' | 'signin';
  // For the userInvitation scenario only: when true the Microsoft button points at the
  // server-provided AAD-only federated login URL (invitationFlags.msaSupported === false).
  aadOnly?: boolean;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
  // Fires just before the redirect to the external IDP. The userInvitation scenario uses it to emit
  // the structured `step=PreAuth outcome=IdpSelected` funnel line.
  // eslint-disable-next-line no-unused-vars
  onIdpSelected?: (provider: string) => void;
}

export const IdpButtonList: React.FC<IdpButtonListProps> = ({
  providers,
  scenario,
  invitationVariant,
  aadOnly,
  logger,
  onIdpSelected,
}) => {
  const serverContext = useContext(ServerContext) as any;
  const i18n = useContext(I18nContext);
  const styles = useStyles();
  const serverLastLoginAuthType = serverContext?.LastLoginAuthType as string | null | undefined;
  const lastLoginAuthType = useMemo<string | null>(() => serverLastLoginAuthType ?? getStoredLastLoginAuthType(), [serverLastLoginAuthType]);

  useEffect(() => {
    if (serverLastLoginAuthType) {
      storeLastLoginAuthType(serverLastLoginAuthType);
    }
  }, [serverLastLoginAuthType]);

  const onClick = useCallback<IdpConfigParams['onClick']>((redirectUrl, provider, index) => {
    // For signup A/B analysis, attach the landing-page visitor id (AdsLandingVisitorId)
    // and the new-landing-page experiment assignment (NewLandingPageExperiment cookie,
    // e.g. treatment1/treatment2) so signup IdP selections can be sliced by treatment
    // cohort in Kusto.
    let abInfo = '';
    if (scenario === 'signup') {
      const { visitorId, experiment } = getAbContext();
      abInfo = `, AdsLandingVisitorId: ${visitorId}, NewLandingPageExperiment: ${experiment}`;
    }
    logger?.trace(`IdpButtonList: Clicked ${provider} button. index: ${index}, scenario: ${scenario}, redirectURL: ${redirectUrl}${abInfo}`);
    onIdpSelected?.(provider);
    window.open(redirectUrl, '_self');
  }, [logger, onIdpSelected, scenario]);

  const configs: IdpButtonConfig[] = useMemo(() => providers.flatMap((provider, index) => {
    if (provider === 'MSA') {
      return getMicrosoftConfig({
        scenario,
        serverContext,
        logger,
        onClick,
        index,
        invitationVariant,
        aadOnly,
      });
    }

    if (provider === 'Google') {
      const shouldShowGoogleAuth = scenario === 'signup'
        ? serverContext?.Permissions?.isGoogleAuthEnabled
        : serverContext?.Permissions?.isGoogleAuthNewSignInPageEnabled;
      if (shouldShowGoogleAuth) {
        return getGoogleConfig({
          scenario,
          serverContext,
          logger,
          onClick,
          index,
          invitationVariant,
        });
      }
      return [];
    }

    if (provider === 'Facebook') {
      const shouldShowFacebookAuth = (scenario === 'signup' || scenario === 'userInvitation')
        ? serverContext?.Permissions?.isFacebookAuthEnabled
        : serverContext?.Permissions?.isFacebookAuthNewSignInPageEnabled;
      if (shouldShowFacebookAuth) {
        return getFacebookConfig({
          scenario,
          serverContext,
          logger,
          onClick,
          index,
          invitationVariant,
        });
      }
      return [];
    }

    // TODO: log unknown provider
    return [];
  }), [logger, onClick, providers, scenario, serverContext, invitationVariant, aadOnly]);

  useEffect(() => {
    logger?.trace(`IdpButtonList: Rendered scenario=${scenario}, configs=[${configs.map(c => c.buttonClassName).join(',')}]`);
  }, [configs, logger, scenario]);

  const lastUsedConfig = scenario === 'signin' && lastLoginAuthType != null
    ? configs.find(config => config.identityProviders.includes(lastLoginAuthType))
    : undefined;

  let lastLoginSource = 'None';
  if (lastLoginAuthType != null) {
    lastLoginSource = serverLastLoginAuthType != null ? 'Cookie' : 'LocalStorage';
  }

  useEffect(() => {
    if (scenario === 'signin') {
      logger?.trace(`IdpButtonList: LastUsedTag shown=${lastUsedConfig != null}, lastLoginAuthType=${lastLoginAuthType}, source=${lastLoginSource}`);
    }
  }, [lastLoginAuthType, lastLoginSource, lastUsedConfig, logger, scenario]);

  // When a provider is highlighted as the primary (blue) option — the Microsoft
  // button in the userInvitation flow — separate it from the remaining outline
  // buttons with an "or" divider, matching the invitation design.
  const primaryConfigs = configs.filter(config => config.appearance === 'primary');
  const secondaryConfigs = configs.filter(config => config.appearance !== 'primary');
  const showDivider = primaryConfigs.length > 0 && secondaryConfigs.length > 0;

  const renderButton = (config: IdpButtonConfig) => (
    <IdpButton
      key={config.buttonClassName}
      {...config}
      textContent={i18n.getString(config.textContent)}
      badgeContent={config === lastUsedConfig
        ? i18n.getString(_TL_('Last used'))
        : undefined}
      wide
    />
  );

  return (
    <div className="idp-button-list">
      {primaryConfigs.map(renderButton)}
      {showDivider && (
        <Divider className={mergeClasses('idp-button-divider', styles.divider)}>
          {i18n.getString(_TL_('or'))}
        </Divider>
      )}
      {secondaryConfigs.map(renderButton)}
    </div>
  );
};
